import React, { useEffect, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  ModalDescription,
  ModalContent,
  ModalActions,
  Button,
  Header,
  Modal,
} from 'semantic-ui-react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import styles from './styles.module.scss';
import shareicon from './google_plus_reshare.svg';
import dowloadicon from './download.svg';
import Disclaimer from './Disclaimer';
import shareIcon from '@plone/volto/icons/share.svg';
import downloadIcon from '@plone/volto/icons/download.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { buildEmbedIframe } from 'volto-base-addon/helpers/embed';
import { buildSubtitleLabel } from 'volto-base-addon/helpers/subtitleLabel';
import { DOWNLOAD_PRESIGNED_ROUTE } from 'volto-base-addon/constants';

const messages = defineMessages({
  closeModal: {
    id: 'Close',
    defaultMessage: 'Close',
  },
  acknowledgementText: {
    id: 'attachmentDownloadAcknowledgementText',
    defaultMessage:
      'I have understood the above and agree to handle the data with care.',
  },
  shareButton: {
    id: 'Downloads.shareButton',
    defaultMessage: 'Share',
  },
  downloadButton: {
    id: 'Downloads.downloadButton',
    defaultMessage: 'Download',
  },
  downloadPopupTitle: {
    id: 'Downloads.downloadPopupTitle',
    defaultMessage: 'Download file',
  },
  exportHeading: {
    id: 'Downloads.exportHeading',
    defaultMessage: 'Export',
  },
  mediaHeading: {
    id: 'Downloads.mediaHeading',
    defaultMessage: 'Media',
  },
  subtitlesHeading: {
    id: 'Downloads.subtitlesHeading',
    defaultMessage: 'Subtitles',
  },
  noTimestamps: {
    id: 'Downloads.noTimestamps',
    defaultMessage: 'no timestamps',
  },
  shareContentTitle: {
    id: 'Downloads.shareContentTitle',
    defaultMessage: 'Share this content',
  },
  sharePrompt: {
    id: 'Downloads.sharePrompt',
    defaultMessage: 'Select a platform to share.',
  },
  embedOption: {
    id: 'Downloads.embedOption',
    defaultMessage: 'Embed',
  },
  emailOption: {
    id: 'Downloads.emailOption',
    defaultMessage: 'Email',
  },
  whatsappOption: {
    id: 'Downloads.whatsappOption',
    defaultMessage: 'Whatsapp',
  },
  facebookOption: {
    id: 'Downloads.facebookOption',
    defaultMessage: 'Facebook',
  },
  twitterOption: {
    id: 'Downloads.twitterOption',
    defaultMessage: 'X',
  },
  embedFieldHeader: {
    id: 'Downloads.embedFieldHeader',
    defaultMessage: 'Include embed code',
  },
  embedPlaceholder: {
    id: 'Downloads.embedPlaceholder',
    defaultMessage: 'Enter HTML embed code here…',
  },
  copyLabel: {
    id: 'Downloads.copyLabel',
    defaultMessage: 'Copy',
  },
  embedAudioToggle: {
    id: 'Downloads.embedAudioToggle',
    defaultMessage: 'Embed audio only',
  },
  toastEmbedCopied: {
    id: 'Downloads.toastEmbedCopied',
    defaultMessage: 'Code copied!',
  },
  toastLinkCopied: {
    id: 'Downloads.toastLinkCopied',
    defaultMessage: 'Link copied to clipboard!',
  },
  timeLinkCheckbox: {
    id: 'Downloads.timeLinkCheckbox',
    defaultMessage: 'Create time link',
  },
  downloadConfirm: {
    id: 'Downloads.downloadConfirm',
    defaultMessage: 'Download',
  },
  EmbedInfo: {
    id: 'Downloads.EmbedInfo',
    defaultMessage:
      'By default, the video is embedded in a smaller size to fit the page’s content area. Select Embed full width if you want the video to span the entire width.',
  },
  embedStyle: {
    id: 'Downloads.embedStyle',
    defaultMessage: 'Embed style',
  },
  embedDefault: {
    id: 'Downloads.embedDefault',
    defaultMessage: 'Default',
  },
  EmbedFullWidth: {
    id: 'Downloads.EmbedFullWidth',
    defaultMessage: 'Embed full width',
  },
  linkedinOption: {
    id: 'Downloads.linkedinOption',
    defaultMessage: 'LinkedIn',
  },
});

function formatFileSize(bytes, { upperBound } = {}) {
  if (!bytes) return null;
  const prefix = upperBound ? '< ' : '';
  if (bytes < 1024) return `${prefix}${bytes} B`;
  if (bytes < 1024 * 1024) return `${prefix}${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${prefix}${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${prefix}${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function customSort(a, b) {
  // Function to check if the title is numeric (resolution)
  const isNumeric = (str) => /\d+p$/.test(str); // Ends with a number followed by 'p'

  const isNumericA = isNumeric(a.title);
  const isNumericB = isNumeric(b.title);

  // Group all non-numeric titles together and sort them alphabetically
  if (!isNumericA && isNumericB) return 1; // Non-numeric titles go after numeric ones
  if (isNumericA && !isNumericB) return -1; // Numeric titles come before non-numeric

  // If both are numeric, sort by the numeric value
  if (isNumericA && isNumericB) {
    const valueA = parseInt(a.title.match(/\d+/)[0], 10);
    const valueB = parseInt(b.title.match(/\d+/)[0], 10);
    return valueA - valueB;
  }

  // If both are non-numeric, sort alphabetically
  return a.title.localeCompare(b.title);
}

const buildDownloadUrl = (uid, params = {}) => {
  const query = new URLSearchParams({
    'content-disposition': 'attachment',
    ...params,
  });
  return `/resolveuid/${uid}/${DOWNLOAD_PRESIGNED_ROUTE}?${query.toString()}`;
};

const Downloads = (props) => {
  const { showDisclaimer, showShareButton } = props;
  const [downloadItems, setDownloadItems] = useState([]);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const content = useSelector((state) => state.content.data);
  const enrichedContent = useSelector(
    (state) => state.mediaPageEnriched?.content,
  );
  const videotitle = content.title;

  const intl = useIntl();
  const [embedType, setEmbedType] = useState('default');

  const UUID = content.UID;

  const toggleEmbedType = () => {
    setEmbedType((prev) => (prev === 'audio' ? 'video' : 'audio'));
    // Jos vaihdetaan audioon, varmuuden vuoksi nollataan kokoleveys
    setFullWidth(false);
  };

  const [fullWidth, setFullWidth] = useState(false); // vain videolle

  const [iframeCode, setIframeCode] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const embedUrl = `${window.location.origin}/embed?uid=${UUID}`;
      setIframeCode(buildEmbedIframe(embedUrl, { type: embedType, fullWidth }));
    }
  }, [UUID, embedType, fullWidth]);

  // Derive download items from enriched content — no per-child API calls.
  // Reactively updates when enrichedContent changes (e.g. transcoding completes).
  useEffect(() => {
    const items = enrichedContent?.items;
    if (!Array.isArray(items)) return;

    const contentItems = items.filter((item) =>
      ['transcoding', 'subtitle'].includes(item['@type']),
    );

    const newDownloadItems = [];
    for (const item of contentItems) {
      if (item.output_state !== 'completed') continue;

      const itemPath = flattenToAppURL(item['@id']);
      const itemId = itemPath.split('/').pop();

      // Served by the express middleware. Addressed by UID rather than path
      // so the link survives renames outright, instead of depending on a
      // p.a.redirector alias that a later object at the old path would drop.
      // UID rides on the enriched fetch (ITEM_METADATA_FIELDS) — this loop
      // makes no per-child request of its own.
      const downloadUrl = buildDownloadUrl(item.UID);

      const displayTitle =
        item['@type'] === 'subtitle' ? buildSubtitleLabel(item) : item.title;

      newDownloadItems.push({
        id: itemId,
        title: displayTitle,
        type: item['@type'],
        downloadUrl: downloadUrl,
        fileSize: item.file_size || null,
        s3_object_path: content.UID,
      });

      if (item['@type'] === 'subtitle') {
        newDownloadItems.push({
          id: `${itemId}-no-timestamps`,
          title: `${displayTitle} (${intl.formatMessage(messages.noTimestamps)})`,
          type: 'subtitle',
          downloadUrl: buildDownloadUrl(item.UID, { format: 'text' }),
          fileSize: item.file_size || null,
          fileSizeIsUpperBound: true,
          s3_object_path: content.UID,
        });
      }
    }
    setDownloadItems(newDownloadItems);
  }, [enrichedContent, content.UID, intl]);
  const downloadButtonHandler = (event) => {
    event.preventDefault();
    if (!selectedItemId) return;

    const selectedItem = downloadItems.find(
      (item) => item.id === selectedItemId,
    );
    if (selectedItem?.downloadUrl) {
      window.location.href = selectedItem.downloadUrl;
    }
  };

  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const [isEmbedFieldOpen, setEmbedFieldOpen] = useState(false);

  const toggleEmbedField = () => {
    setEmbedFieldOpen(!isEmbedFieldOpen);
  };

  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    // check if the window is available
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, []);

  const [showToastEmbed, setShowToastEmbed] = useState(false);
  const [showToastUrl, setShowToastUrl] = useState(false);
  const inputRef = useRef(null);

  const copyToClipboard = () => {
    if (inputRef.current) {
      inputRef.current.select();
      inputRef.current.setSelectionRange(0, 99999); // Mobile support

      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          // Show toast notification
          setShowToastUrl(true);
          setTimeout(() => setShowToastUrl(false), 3000);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Kopiointi epäonnistui: ', err);
        });
    }
  };

  const textareaRef = useRef(null);

  const handleCopy = () => {
    if (textareaRef.current) {
      textareaRef.current.select();
      textareaRef.current.setSelectionRange(0, 99999); // Mobile support

      navigator.clipboard
        .writeText(textareaRef.current.value)
        .then(() => {
          setShowToastEmbed(true);
          setTimeout(() => setShowToastEmbed(false), 3000);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Kopiointi epäonnistui: ', err);
        });
    }
  };

  const shareToWhatsApp = () => {
    const whatsappUrl = `https://api.whatsapp.com/send?text=Katso%20tämä%20Moniviestin-video%20${encodeURIComponent(
      videotitle,
    )}%3A%20${encodeURIComponent(currentUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      currentUrl,
    )}`;
    window.open(facebookUrl, '_blank');
  };

  // const shareToTwitter = () => {
  //   const tweetText = `Katso tämä Moniviestin-video: ${videotitle}: `;
  //   const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
  //     tweetText,
  //   )}${encodeURIComponent(currentUrl)}`;
  //   window.open(twitterUrl, '_blank');
  // };
  const shareToEmail = () => {
    const subject = 'Katso tämä Moniviestin-video!';
    const body = `Katso tämä Moniviestin-video ${videotitle}: ${currentUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const shareToLinkedIn = () => {
    const urlToShare =
      (typeof window !== 'undefined' && window.location.href) || currentUrl;

    if (!urlToShare) return; // varmistus

    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      urlToShare,
    )}`;

    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    // Varmistaa, että toastit poistetaan, kun näkymä vaihtuu
    return () => {
      setShowToastEmbed(false);
      setShowToastUrl(false);
    };
  }, []);

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  useEffect(() => {
    if (isChecked) {
      const timeElement = document.querySelector('.vjs-current-time-display');
      if (timeElement) {
        const time = timeElement.textContent;
        setTimeValue(time);
      }
    } else {
      setTimeValue('');
    }
  }, [isChecked]);

  const [timeValue, setTimeValue] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined' && timeValue) {
      const totalSeconds = convertTimeToSeconds(timeValue);
      let url = new URL(window.location.href);
      url.searchParams.set('t', totalSeconds);
      setCurrentUrl(url.toString());
    }
  }, [timeValue]);

  const handleTimeChange = (e) => {
    setTimeValue(e.target.value);
  };

  const convertTimeToSeconds = (time) => {
    const parts = time.split(':');
    if (parts.length === 1) {
      // Jos käyttäjä syöttää vain minuutit, esim. "12", muutetaan se sekunneiksi
      return parseInt(parts[0], 10) * 60;
    } else if (parts.length === 2) {
      // Jos käyttäjä syöttää muodossa "mm:ss"
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);
      return minutes * 60 + seconds;
    }
    return 0;
  };

  const slideLeft = () => {
    document.querySelector(`.${styles.sharerowWrapper}`)?.scrollBy({
      left: -100,
      behavior: 'smooth',
    });
  };

  const slideRight = () => {
    document.querySelector(`.${styles.sharerowWrapper}`)?.scrollBy({
      left: 100,
      behavior: 'smooth',
    });
  };

  return (
    (downloadItems.length > 0 || showShareButton) && (
      <div className="downloads-container block">
        <div className={styles.downloadsContainer}>
          {showShareButton && (
            <Modal
              className="media-share-modal"
              closeIcon
              onClose={() => {
                setEmbedFieldOpen(false);
                setIsSharePopupOpen(false);
              }}
              onOpen={() => setIsSharePopupOpen(true)}
              open={isSharePopupOpen}
              trigger={
                <button className={styles.button}>
                  {intl.formatMessage(messages.shareButton)}
                  <img src={shareicon} alt="Icon" className={styles.icon} />
                </button>
              }
            >
              <Header>
                <i aria-hidden="true" className="icon">
                  <Icon name={shareIcon} />
                </i>
                <div className="content">
                  {intl.formatMessage(messages.shareContentTitle)}
                </div>
              </Header>

              <ModalContent>
                <ModalDescription>
                  {!isEmbedFieldOpen && (
                    <center>{intl.formatMessage(messages.sharePrompt)}</center>
                  )}
                </ModalDescription>
                {!isEmbedFieldOpen && (
                  <div>
                    <div className={styles.sharerowWrapper}>
                      <button className={styles.arrowLeft} onClick={slideLeft}>
                        ‹
                      </button>
                      <div className={styles.sharerow}>
                        <div
                          className={styles.share}
                          onClick={toggleEmbedField}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              toggleEmbedField();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`${styles.embed} ${styles.shareround}`}
                          ></div>
                          <p>{intl.formatMessage(messages.embedOption)}</p>
                        </div>

                        <div
                          className={styles.share}
                          onClick={shareToEmail}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              shareToEmail();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`${styles.email} ${styles.shareround}`}
                          ></div>
                          <p>{intl.formatMessage(messages.emailOption)}</p>
                        </div>

                        <div
                          className={styles.share}
                          onClick={shareToWhatsApp}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              shareToWhatsApp();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`${styles.whatsapp} ${styles.shareround}`}
                          ></div>
                          <p>{intl.formatMessage(messages.whatsappOption)}</p>
                        </div>

                        <div
                          className={styles.share}
                          onClick={shareToFacebook}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              shareToFacebook();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`${styles.facebook} ${styles.shareround}`}
                          ></div>
                          <p>{intl.formatMessage(messages.facebookOption)}</p>
                        </div>
                        <div
                          className={styles.share}
                          onClick={shareToLinkedIn}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                              shareToLinkedIn();
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`${styles.linkedin} ${styles.shareround}`}
                          ></div>
                          <p>{intl.formatMessage(messages.linkedinOption)}</p>
                        </div>
                      </div>
                    </div>
                    <button className={styles.arrowRight} onClick={slideRight}>
                      ›
                    </button>
                  </div>
                )}

                {/* Embed field*/}
                {isEmbedFieldOpen && (
                  <div className="embed-bar">
                    <h3>{intl.formatMessage(messages.embedFieldHeader)}</h3>
                    <p>{intl.formatMessage(messages.EmbedInfo)}</p>
                    <div className={styles.embedField}>
                      <textarea
                        placeholder={intl.formatMessage(
                          messages.embedPlaceholder,
                        )}
                        rows="5"
                        className={styles.textarea}
                        value={iframeCode}
                        readOnly
                        ref={textareaRef}
                      />
                    </div>

                    <div className={styles.embedfieldcopy}>
                      <div
                        className={styles.embedTypeToggleContainer}
                        role="radiogroup"
                        aria-labelledby="embedStyleHeader"
                      >
                        <h4 id="embedStyleHeader">
                          {intl.formatMessage(messages.embedStyle)}
                        </h4>
                        <div>
                          <input
                            id="embedTypeDefault"
                            type="radio"
                            name="embedType"
                            value="default"
                            onChange={(e) =>
                              setEmbedType(e.currentTarget.value)
                            }
                            checked={embedType == 'default'}
                          />
                          <label htmlFor="embedTypeDefault">
                            {intl.formatMessage(messages.embedDefault)}
                          </label>
                        </div>
                        <div>
                          <input
                            id="embedTypeFullWidth"
                            type="radio"
                            name="embedType"
                            value="fullWidth"
                            onChange={(e) =>
                              setEmbedType(e.currentTarget.value)
                            }
                            checked={embedType == 'fullWidth'}
                          />
                          <label htmlFor="embedTypeFullWidth">
                            {intl.formatMessage(messages.EmbedFullWidth)}
                          </label>
                        </div>
                        <div>
                          <input
                            id="embedTypeAudioOnly"
                            type="radio"
                            name="embedType"
                            value="audioOnly"
                            onChange={(e) =>
                              setEmbedType(e.currentTarget.value)
                            }
                            checked={embedType == 'audioOnly'}
                          />
                          <label htmlFor="embedTypeAudioOnly">
                            {intl.formatMessage(messages.embedAudioToggle)}
                          </label>
                        </div>
                      </div>

                      <button
                        className={styles.copyButton}
                        onClick={handleCopy}
                      >
                        {intl.formatMessage(messages.copyLabel)}
                      </button>
                    </div>
                    {showToastEmbed && (
                      <div className={styles.toast}>
                        {intl.formatMessage(messages.toastEmbedCopied)}
                      </div>
                    )}
                  </div>
                )}

                {!isEmbedFieldOpen && (
                  <div className="copy-link-bar">
                    <div className={styles.urlFieldContainer}>
                      <input
                        type="text"
                        value={currentUrl}
                        readOnly
                        ref={inputRef}
                        className={styles.urlField}
                      />

                      <button
                        onClick={copyToClipboard}
                        className={styles.copyButton}
                      >
                        {intl.formatMessage(messages.copyLabel)}
                      </button>

                      {showToastUrl && (
                        <div className={styles.toast}>
                          {intl.formatMessage(messages.toastLinkCopied)}
                        </div>
                      )}
                    </div>
                    <div className={styles.checkboxcontainer}>
                      <label>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                          className={styles.timelinkcheckbox}
                        />
                        {intl.formatMessage(messages.timeLinkCheckbox)}
                      </label>
                      {isChecked && (
                        <input
                          type="text"
                          placeholder="00:00"
                          value={timeValue}
                          onChange={handleTimeChange}
                          className={styles.timeinput}
                        />
                      )}
                    </div>
                  </div>
                )}
              </ModalContent>
              <ModalActions>
                <Button
                  color="black"
                  onClick={() => {
                    setEmbedFieldOpen(false);
                    setIsSharePopupOpen(false);
                  }}
                >
                  {intl.formatMessage(messages.closeModal)}
                </Button>
              </ModalActions>
            </Modal>
          )}
          {!content.prevent_downloads && downloadItems.length > 0 && (
            <Modal
              className="media-download-modal"
              closeIcon
              onClose={() => {
                setIsAcknowledged(false);
                setIsDownloadPopupOpen(false);
              }}
              onOpen={() => setIsDownloadPopupOpen(true)}
              open={isDownloadPopupOpen}
              trigger={
                <button className={styles.button}>
                  {intl.formatMessage(messages.downloadButton)}
                  <img src={dowloadicon} alt="Icon" className={styles.icon} />
                </button>
              }
            >
              <Header>
                <i aria-hidden="true" className="icon">
                  <Icon name={downloadIcon} />
                </i>
                <div className="content">
                  {intl.formatMessage(messages.downloadPopupTitle)}
                </div>
              </Header>
              <ModalContent>
                {showDisclaimer && (
                  <div className="downloads-disclaimer-text">
                    <Disclaimer />
                  </div>
                )}
                {downloadItems.filter((item) => item['type'] === 'transcoding')
                  .length > 0 && (
                  <p className={styles.sectionHeading}>
                    {intl.formatMessage(messages.mediaHeading)}
                  </p>
                )}
                {showDisclaimer ? (
                  <>
                    {downloadItems
                      .filter((item) => item['type'] === 'transcoding')
                      .sort(customSort)
                      .map((item) => (
                        <div key={item.id} style={{ marginBottom: '10px' }}>
                          <input
                            type="radio"
                            id={`downloadItem-${item.id}`}
                            name="downloadItem"
                            value={item.id}
                            checked={selectedItemId === item.id}
                            onChange={(e) => setSelectedItemId(e.target.value)}
                            style={{ marginRight: '5px' }}
                          />
                          <label htmlFor={`downloadItem-${item.id}`}>
                            {item.title}
                            {item.fileSize && (
                              <span className={styles.fileSize}>
                                {formatFileSize(item.fileSize, {
                                  upperBound: item.fileSizeIsUpperBound,
                                })}
                              </span>
                            )}
                          </label>
                        </div>
                      ))}
                    {downloadItems.filter((item) => item['type'] === 'subtitle')
                      .length > 0 && (
                      <p className={styles.sectionHeading}>
                        {intl.formatMessage(messages.subtitlesHeading)}
                      </p>
                    )}
                    {downloadItems
                      .filter((item) => item['type'] === 'subtitle')
                      .sort(customSort)
                      .map((item) => (
                        <div key={item.id} style={{ marginBottom: '10px' }}>
                          <input
                            type="radio"
                            id={`downloadItem-${item.id}`}
                            name="downloadItem"
                            value={item.id}
                            checked={selectedItemId === item.id}
                            onChange={(e) => setSelectedItemId(e.target.value)}
                            style={{ marginRight: '5px' }}
                          />
                          <label htmlFor={`downloadItem-${item.id}`}>
                            {item.title}
                            {item.fileSize && (
                              <span className={styles.fileSize}>
                                {formatFileSize(item.fileSize, {
                                  upperBound: item.fileSizeIsUpperBound,
                                })}
                              </span>
                            )}
                          </label>
                        </div>
                      ))}
                    <label style={{ display: 'block', marginTop: '20px' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '10px' }}
                        checked={isAcknowledged}
                        onChange={() => setIsAcknowledged(!isAcknowledged)}
                      />
                      {intl.formatMessage(messages.acknowledgementText)}
                    </label>
                  </>
                ) : (
                  <>
                    {downloadItems
                      .filter((item) => item['type'] === 'transcoding')
                      .sort(customSort)
                      .map((item) => (
                        <div key={item.id} className={styles.downloadRow}>
                          <a
                            href={item.downloadUrl}
                            className={styles.downloadLink}
                          >
                            {item.title}
                            {item.fileSize && (
                              <span className={styles.fileSize}>
                                {formatFileSize(item.fileSize, {
                                  upperBound: item.fileSizeIsUpperBound,
                                })}
                              </span>
                            )}
                          </a>
                        </div>
                      ))}
                    {downloadItems.filter((item) => item['type'] === 'subtitle')
                      .length > 0 && (
                      <p className={styles.sectionHeading}>
                        {intl.formatMessage(messages.subtitlesHeading)}
                      </p>
                    )}
                    {downloadItems
                      .filter((item) => item['type'] === 'subtitle')
                      .sort(customSort)
                      .map((item) => (
                        <div key={item.id} className={styles.downloadRow}>
                          <a
                            href={item.downloadUrl}
                            className={styles.downloadLink}
                          >
                            {item.title}
                            {item.fileSize && (
                              <span className={styles.fileSize}>
                                {formatFileSize(item.fileSize, {
                                  upperBound: item.fileSizeIsUpperBound,
                                })}
                              </span>
                            )}
                          </a>
                        </div>
                      ))}
                  </>
                )}
              </ModalContent>
              <ModalActions>
                <Button
                  color="black"
                  onClick={() => {
                    setIsAcknowledged(false);
                    setIsDownloadPopupOpen(false);
                  }}
                >
                  {intl.formatMessage(messages.closeModal)}
                </Button>
                {showDisclaimer && (
                  <Button
                    onClick={downloadButtonHandler}
                    disabled={!selectedItemId || !isAcknowledged}
                  >
                    {intl.formatMessage(messages.downloadConfirm)}
                  </Button>
                )}
              </ModalActions>
            </Modal>
          )}
        </div>
      </div>
    )
  );
};

export default Downloads;
